import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { buildAppPath, buildNavigationPath } from '@/utils/agencyRouting';
import {
  consumePricingIntent,
  clearPricingRedirecting,
  markPricingError,
} from '@/components/lead-gen-website/pricingIntent';
import { bootstrapAndRedirectForIntent } from '@/components/lead-gen-website/agencyCheckoutBootstrap';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { trackEvent } from '@/lib/userTracking';
import { maskPrivatePath } from '@/lib/privatePaths';
import { peekAppsumoLicense, captureAppsumoLicenseFromUrl } from '@/lib/appsumoLicense';
import { forgetInviteChecksOnInvitePage, onceForUser } from './profileSetupOnce';
import { runUserProfileSetup } from './profileSetup';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to check if user is authorized for the current agency domain
const checkAgencyAuthorization = async (userId: string): Promise<{ authorized: boolean; reason?: string }> => {
  // Only check on agency domains
  const agencyBranding = typeof window !== 'undefined' ? (window as any).__AGENCY_BRANDING__ : null;
  if (!agencyBranding?.id) {
    // Not on agency domain - no restriction
    return { authorized: true };
  }

  // Skip auth check on agency-onboarding page with a token — user is mid-onboarding
  // and hasn't been assigned as owner/member yet. The onboarding page validates the token itself.
  const currentParams = new URLSearchParams(window.location.search);
  if (window.location.pathname.includes('agency-onboarding') && currentParams.get('token')) {
    console.log('✅ Skipping agency auth check — user is on onboarding page with token');
    return { authorized: true };
  }

  const agencyId = agencyBranding.id;

  // Check sessionStorage cache first (avoids re-querying on page reloads/workspace switches)
  const cacheKey = `agency_auth_${userId}_${agencyId}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const { authorized, timestamp } = JSON.parse(cached);
      if (authorized && Date.now() - timestamp < 5 * 60 * 1000) { // 5 minute TTL
        console.log('🔐 Using cached agency authorization: authorized');
        return { authorized: true };
      }
    }
  } catch { /* ignore parse errors */ }

  console.log('🔐 Checking agency authorization for user:', userId, 'agency:', agencyId);

  // Add timeout wrapper - if check takes too long, allow access (better UX)
  // Unauthorized users will be caught on subsequent checks
  const timeoutPromise = new Promise<{ authorized: boolean; reason?: string }>((resolve) => {
    setTimeout(() => {
      console.log('⚠️ Agency authorization check timed out - allowing access');
      resolve({ authorized: true });
    }, 5000); // 5 second timeout
  });

  const authCheckPromise = (async () => {
    try {
      // Check 1: Is user the agency owner? (most common - check first)
      const { data: agency, error: agencyError } = await supabase
        .from('agencies')
        .select('owner_id')
        .eq('id', agencyId)
        .single();

      if (agencyError) {
        console.error('Error fetching agency:', agencyError);
        // On error, allow access - better UX than blocking legitimate users
        return { authorized: true };
      }

      if (agency?.owner_id === userId) {
        console.log('✅ User is agency owner - authorized');
        return { authorized: true };
      }

      // Run remaining checks in parallel for speed
      const [agencyUserResult, workspaceResult] = await Promise.all([
        // Check 2: Is user in agency_users with active status?
        supabase
          .from('agency_users')
          .select('id')
          .eq('agency_id', agencyId)
          .eq('user_id', userId)
          .eq('invitation_status', 'active')
          .limit(1)
          .maybeSingle(),

        // Check 3: Does user have a workspace under this agency?
        supabase
          .from('workspaces')
          .select('id')
          .eq('agency_id', agencyId)
          .eq('owner_id', userId)
          .limit(1)
          .maybeSingle(),
      ]);

      if (agencyUserResult.data) {
        console.log('✅ User is agency team member - authorized');
        return { authorized: true };
      }

      if (workspaceResult.data) {
        console.log('✅ User has workspace under agency - authorized');
        return { authorized: true };
      }

      // Check 4: Is user a member of any workspace under this agency?
      // Check both workspace_members (legacy) and workspace_users (team invites)
      const [workspaceMemberResult, workspaceUserResult] = await Promise.all([
        supabase
          .from('workspace_members')
          .select('workspace_id, workspaces!inner(agency_id)')
          .eq('user_id', userId)
          .eq('workspaces.agency_id', agencyId)
          .limit(1)
          .maybeSingle(),
        supabase
          .from('workspace_users')
          .select('workspace_id, workspaces!inner(agency_id)')
          .eq('user_id', userId)
          .eq('invitation_status', 'active')
          .eq('workspaces.agency_id', agencyId)
          .limit(1)
          .maybeSingle(),
      ]);

      if (workspaceMemberResult.data) {
        console.log('✅ User is workspace member under agency - authorized');
        return { authorized: true };
      }

      if (workspaceUserResult.data) {
        console.log('✅ User is workspace team member (via invite) under agency - authorized');
        return { authorized: true };
      }

      // User is not authorized
      console.log('❌ User is not authorized for this agency');
      return {
        authorized: false,
        reason: `You don't have access to this portal. Please contact ${agencyBranding.brandName || 'the agency'} administrator to request access.`
      };
    } catch (error) {
      console.error('Agency authorization check failed:', error);
      // On error, allow access - better UX than blocking legitimate users
      return { authorized: true };
    }
  })();

  // Return whichever completes first
  const result = await Promise.race([authCheckPromise, timeoutPromise]);

  // Cache authorized results to avoid re-querying on page reloads
  if (result.authorized) {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ authorized: true, timestamp: Date.now() }));
    } catch { /* ignore storage errors */ }
  }

  return result;
};

async function handlePostOAuthPricingIntent(): Promise<boolean> {
  // Returns true if the page is about to navigate (caller should skip the
  // rest of post-OAuth handling).
  //
  // An in-flight AppSumo license claim owns the post-OAuth journey: a stale
  // pricing intent (stashed by a CTA the user abandoned at the Google screen)
  // must not hard-navigate the buyer into the paid checkout funnel mid-claim.
  // The intent stays stashed and resumes normally once the claim is done.
  if (peekAppsumoLicense()) return false;
  const intent = consumePricingIntent();
  if (!intent) return false;
  try {
    // Runs the (idempotent) bootstrap with a hard timeout, marks the redirect
    // flag so the shell keeps its loading screen during the ~2s wait, and
    // hard-navigates to checkout/tools on success. Throws on any failure.
    await bootstrapAndRedirectForIntent(intent);
    return true;
  } catch (err) {
    // Setup failed (error, no agency, or timeout). Don't silently drop a buyer
    // on the dashboard: clear the loading flag and record the failed intent so
    // the app shell renders the recoverable PricingSetupError screen (Try
    // again / Contact support) with the chosen plan preserved.
    console.error('Pricing-intent bootstrap failed:', err);
    clearPricingRedirecting();
    markPricingError(intent, {
      code: (err as { code?: string })?.code,
      message: (err as Error)?.message,
    });
    return false;
  }
}

// The "Create account" CTA now hard-wires its OAuth redirect target to
// /fly-app/welcome, so AuthProvider does not need to detect a signup
// intent or interpose a redirect itself. The welcome page self-
// bootstraps when ?agency is missing. This removes a fragile flag-
// based handshake that was sensitive to HMR / OAuth callback timing.

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Check if we have a cached session to avoid initial loading state
  const getCachedUser = () => {
    try {
      const keys = Object.keys(localStorage);
      return keys.some(key => key.startsWith('sb-') && key.endsWith('-auth-token'));
    } catch {
      return false;
    }
  };

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(() => {
    // Only show loading if no cached session exists
    return !getCachedUser();
  });
  const { toast } = useToast();
  const signInToastShown = useRef(false);
  const isInitialized = useRef(false);
  const currentUserId = useRef<string | undefined>(undefined);

  // Suppress SIGNED_OUT toast while we are processing an OAuth/magic-link callback.
  // setSession() fires SIGNED_OUT before SIGNED_IN, which would show a false "Signed out" toast.
  const processingOAuthCallback = useRef(false);

  // Opening the lead-gen invite page means the next load asks about invites again.
  useEffect(() => {
    forgetInviteChecksOnInvitePage(window.location.pathname);
  }, []);

  // Watchdog: hard ceiling on how long `loading` can stay true. Mirrors the
  // 15s failsafe LocalSEOApp uses for the pricing-redirect window. If any
  // path in getInitialSession / onAuthStateChange hangs (slow Supabase
  // token swap, third-party script wedging the microtask queue, etc.), the
  // user MUST NOT be trapped on the "Loading…" spinner forever — every
  // downstream guard (SubscriptionGuard, LocalSEOApp's BrandedLoadingScreen)
  // gates on this `loading` flag. After 10s we force-release with a
  // console.warn so future debugging sees we hit the failsafe.
  useEffect(() => {
    const watchdog = window.setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn(
            '[AuthProvider] watchdog: forcing loading=false after 10s — auth path stalled',
          );
        }
        return false;
      });
    }, 10_000);
    return () => window.clearTimeout(watchdog);
  }, []);

  // Global AppSumo safety net — makes the redemption flow compulsory
  // once a code has been stashed. Re-fires on EVERY route change (via
  // useLocation dep) so an in-app navigation to /fly-app from anywhere
  // is caught, not just the initial mount. If a pending AppSumo code
  // sits in localStorage and we're anywhere other than /redeem or
  // /redeem/welcome, bounce back to /redeem so the auto-reserve effect
  // there can complete the flow.
  //
  // Enforced compulsory sequence for any buyer who pasted a code:
  //   /redeem  →  welcome form  →  Go to dashboard  →  lead-gen-tools
  // The pending_appsumo_code key is only cleared after a successful
  // commit (SuccessCard rendered) or an explicit Cancel on the
  // welcome form. Until then, every SPA navigation snaps back to
  // /redeem — no escape hatch to /fly-app dashboards until the code
  // is either committed or cancelled.
  const location = useLocation();
  const navigate = useNavigate();
  // When a pending AppSumo code is detected while we're on a non-redeem
  // route, we set this true SYNCHRONOUSLY at first render so the splash
  // renders on paint zero — no /fly-app flash before the redirect fires.
  // Then we kick off an SPA navigate() so the transition to /redeem is
  // smooth (no full-page reload, no bundle re-download).
  const shouldSplashForAppSumo = (() => {
    if (typeof window === 'undefined') return false;
    // The LICENSE net below takes priority: if both a stale pending code and a
    // fresh license stash exist, the two nets would navigate against each
    // other forever (splash ping-pong, neither page ever mounts). License wins
    // because it is the live deal; the code flow is dormant.
    if (peekAppsumoLicense()) return false;
    try {
      if (!localStorage.getItem('pending_appsumo_code')) return false;
    } catch {
      return false;
    }
    const p = window.location.pathname;
    return p !== '/redeem' && p !== '/redeem/welcome';
  })();
  useEffect(() => {
    if (!shouldSplashForAppSumo) return;
    console.log('🎟️  AppSumo pending code detected on', location.pathname, '— navigating to /redeem');
    navigate('/redeem', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, shouldSplashForAppSumo]);

  // Same compulsory-flow pattern for the AppSumo LICENSE flow (Licensing API
  // v2). Once a license key is stashed (captured from ?appsumo_license by
  // main.tsx or /appsumo/welcome itself), every navigation snaps back to
  // /appsumo/welcome until the page finishes the claim. The welcome page owns
  // the actual redemption and clears the stash on success or on a terminal
  // error (claimed by another account, dead license), which releases this
  // net. This guarantees the buyer cannot lose the flow even when the OAuth
  // return drops them on /fly-app instead of the welcome page (the Supabase
  // redirect allowlist fallback that was silently eating fresh-login claims).
  const shouldSplashForAppsumoLicense = (() => {
    if (typeof window === 'undefined') return false;
    captureAppsumoLicenseFromUrl(window.location.search);
    if (!peekAppsumoLicense()) return false;
    // BOTH claim pages must be exempt. appsumo_licenses is shared with
    // ClinicLeads, and naming only the lead-gen page here meant a stashed
    // CLINIC licence was snapped straight into the lead-gen claim, which then
    // quoted 18,000 credits for a 900 screening purchase. Verified: loading
    // /appsumo/clinic/welcome redirected to /appsumo/welcome.
    const claimPaths = ['/appsumo/welcome', '/appsumo/clinic/welcome'];
    return !claimPaths.includes(window.location.pathname);
  })();
  useEffect(() => {
    if (!shouldSplashForAppsumoLicense) return;
    console.log('🎟️  AppSumo pending license detected on', location.pathname, '- navigating to /appsumo/welcome');
    navigate('/appsumo/welcome', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, shouldSplashForAppsumoLicense]);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      // Fallback: For non-whitelisted agency domains, fly-social.com receives the OAuth callback
      // and forwards tokens back to the agency domain via hash fragment redirect.
      {
        const earlyHash = new URLSearchParams(window.location.hash.substring(1));
        const earlyAccessToken = earlyHash.get('access_token');
        const earlyRefreshToken = earlyHash.get('refresh_token');
        if (earlyAccessToken && earlyRefreshToken) {
          const agencyOrigin = sessionStorage.getItem('oauth_agency_origin');
          if (agencyOrigin) {
            console.log('🏢 Agency OAuth callback (fallback) — redirecting tokens to agency:', agencyOrigin);
            sessionStorage.removeItem('oauth_agency_origin');
            const agencyRedirectUrl = `${agencyOrigin}/#access_token=${earlyAccessToken}&refresh_token=${earlyRefreshToken}&token_type=bearer`;
            window.location.replace(agencyRedirectUrl);
            return;
          }
        }
      }

      try {
        // Check if we're coming back from OAuth/magic link (hash contains tokens)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasOAuthTokens = hashParams.has('access_token');

        // If we have tokens in the URL hash, we MUST process them
        // even if we already have a session (this handles impersonation)
        if (hasOAuthTokens) {
          console.log('📝 OAuth/Magic link callback detected, processing tokens...');

          // Extract tokens from URL hash
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');

          if (accessToken && refreshToken) {
            // Use setSession to explicitly set the new session from magic link.
            // This is necessary when impersonating because getSession would return the old session.
            console.log('🔄 Setting new session from magic link tokens...');
            // Prevent the SIGNED_OUT event (fired by setSession before SIGNED_IN)
            // from showing a false "Signed out" toast to the user.
            processingOAuthCallback.current = true;
            const { data: { session }, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            processingOAuthCallback.current = false;
            if (error) {
              console.error('Error setting session from magic link:', error);
              toast({
                title: "Authentication Error",
                description: "Failed to complete sign-in. Please try again.",
                variant: "destructive"
              });
            } else if (session?.user) {
              console.log('✅ Magic link session set, user:', session.user.email);
              setUser(session.user);
              currentUserId.current = session.user.id;
              setSentryUser({ id: session.user.id, email: session.user.email });

              // Ensure profile exists
              ensureUserProfile(session.user).catch(err =>
                console.error('Profile setup error (non-critical):', err)
              );

              // Check for pending agency onboarding token BEFORE auth check
              // (user is mid-onboarding and not yet authorized for the agency)
              const pendingOnboardingTokenOAuth = sessionStorage.getItem('pending_agency_onboarding_token');
              if (pendingOnboardingTokenOAuth) {
                console.log('🔗 Pending agency onboarding token found, skipping auth check and redirecting');
                sessionStorage.removeItem('pending_agency_onboarding_token');
                if (window.location.hash) {
                  window.history.replaceState({}, '', window.location.pathname + window.location.search);
                }
                window.location.href = buildNavigationPath(`/agency-onboarding?token=${pendingOnboardingTokenOAuth}`);
                return;
              }

              // Check for pending invite tokens BEFORE auth check
              // (user just signed up/in to accept an invite — not yet authorized for the agency)
              const pendingInviteTokenOAuth = sessionStorage.getItem('pending_invite_token');
              const pendingAgencyInviteTokenOAuth = sessionStorage.getItem('pending_agency_invite_token');
              const pendingInviteOrigin = sessionStorage.getItem('pending_invite_origin');
              if (pendingAgencyInviteTokenOAuth) {
                console.log('🔗 Pending agency invite token found, skipping auth check and redirecting');
                sessionStorage.removeItem('pending_agency_invite_token');
                sessionStorage.removeItem('invite_email');
                sessionStorage.removeItem('pending_invite_origin');
                if (window.location.hash) {
                  window.history.replaceState({}, '', window.location.pathname + window.location.search);
                }
                // Redirect back to the agency domain if the invite originated there
                const baseUrl = pendingInviteOrigin || buildAppPath('/');
                window.location.href = `${baseUrl}/?agency-invite=${pendingAgencyInviteTokenOAuth}`;
                return;
              }
              if (pendingInviteTokenOAuth) {
                console.log('🔗 Pending workspace invite token found, skipping auth check and redirecting');
                sessionStorage.removeItem('pending_invite_token');
                sessionStorage.removeItem('invite_email');
                sessionStorage.removeItem('pending_invite_origin');
                if (window.location.hash) {
                  window.history.replaceState({}, '', window.location.pathname + window.location.search);
                }
                // Redirect back to the agency domain if the invite originated there
                const baseUrl = pendingInviteOrigin || buildAppPath('/');
                window.location.href = `${baseUrl}/?invite-link=${pendingInviteTokenOAuth}`;
                return;
              }

              // Check for pending pricing intent (public pricing CTA after OAuth)
              if (await handlePostOAuthPricingIntent()) {
                // Hard-navigation in progress; nothing else to do.
                return;
              }

              // Check agency authorization on agency domains
              const agencyAuth = await checkAgencyAuthorization(session.user.id);
              if (!agencyAuth.authorized) {
                console.log('❌ User not authorized for agency, signing out...');
                // Preserve agency onboarding token before sign-out
                const urlParams = new URLSearchParams(window.location.search);
                const onboardingToken = urlParams.get('token');
                if (window.location.pathname.includes('agency-onboarding') && onboardingToken) {
                  sessionStorage.setItem('pending_agency_onboarding_token', onboardingToken);
                }
                await supabase.auth.signOut();
                setUser(null);
                currentUserId.current = undefined;
                toast({
                  title: "Access Denied",
                  description: agencyAuth.reason || "You don't have access to this portal.",
                  variant: "destructive"
                });
                setLoading(false);
                isInitialized.current = true;
                return;
              }
            }
          } else {
            // Fallback to getSession if tokens are not in expected format
            console.log('⚠️ Tokens not found in hash, falling back to getSession...');
            const { data: { session }, error } = await supabase.auth.getSession();
            if (!error && session?.user) {
              // Check agency authorization before setting user
              const agencyAuth = await checkAgencyAuthorization(session.user.id);
              if (!agencyAuth.authorized) {
                console.log('❌ User not authorized for agency, signing out...');
                // Preserve agency onboarding token before sign-out
                const urlParams2 = new URLSearchParams(window.location.search);
                const onboardingToken2 = urlParams2.get('token');
                if (window.location.pathname.includes('agency-onboarding') && onboardingToken2) {
                  sessionStorage.setItem('pending_agency_onboarding_token', onboardingToken2);
                }
                await supabase.auth.signOut();
                toast({
                  title: "Access Denied",
                  description: agencyAuth.reason || "You don't have access to this portal.",
                  variant: "destructive"
                });
                setLoading(false);
                isInitialized.current = true;
                return;
              }
              setUser(session.user);
              currentUserId.current = session.user.id;
              setSentryUser({ id: session.user.id, email: session.user.email });
            }
          }

          // Clean up the URL hash
          if (window.location.hash) {
            window.history.replaceState({}, '', window.location.pathname + window.location.search);
          }

          // Check for pending agency onboarding token (from Access Denied → Try Again flow)
          const pendingOnboardingTokenInit = sessionStorage.getItem('pending_agency_onboarding_token');
          if (pendingOnboardingTokenInit) {
            sessionStorage.removeItem('pending_agency_onboarding_token');
            console.log('🔗 Redirecting to agency onboarding with preserved token');
            window.location.href = buildNavigationPath(`/agency-onboarding?token=${pendingOnboardingTokenInit}`);
            return;
          }

          // AppSumo LICENSE claim in flight: it owns the post-OAuth journey.
          // Bounce straight to /appsumo/welcome (the page that completes the
          // claim) and do NOT consume the code net or oauth_redirect_url
          // below, so neither can hijack the buyer mid-claim.
          if (peekAppsumoLicense()) {
            const p = window.location.pathname;
            if (p !== '/appsumo/welcome') {
              console.log('🎟️  AppSumo pending license detected - redirecting to /appsumo/welcome');
              window.location.replace('/appsumo/welcome');
              return;
            }
          } else {
            // AppSumo CODE redemption safety net. If the buyer pasted a code on
            // /redeem and OAuth landed them somewhere other than /redeem
            // (e.g. Supabase redirect allowlist rejected /redeem and fell
            // back to /fly-app), the pending code sits in localStorage
            // unused. Bounce back to /redeem so the auto-reserve effect
            // there can pick it up and complete the flow.
            try {
              const pendingAppSumoCode = localStorage.getItem('pending_appsumo_code');
              if (pendingAppSumoCode) {
                const p = window.location.pathname;
                if (p !== '/redeem' && p !== '/redeem/welcome') {
                  console.log('🎟️  AppSumo pending code detected - redirecting to /redeem');
                  window.location.replace('/redeem');
                  return;
                }
              }
            } catch {
              /* ignore storage errors */
            }
          }

          // After OAuth, redirect back to the original URL the user was trying
          // to access. Skipped while a license claim is pending (handled above).
          const oauthRedirectUrl = localStorage.getItem('oauth_redirect_url');
          if (oauthRedirectUrl && !peekAppsumoLicense()) {
            localStorage.removeItem('oauth_redirect_url');
            // Only redirect if it's a different path (not already there)
            const targetPath = oauthRedirectUrl.startsWith('/') ? oauthRedirectUrl : `/${oauthRedirectUrl}`;
            if (window.location.pathname + window.location.search !== targetPath) {
              console.log('🔗 Redirecting to pre-OAuth URL:', targetPath);
              window.location.replace(targetPath);
              return;
            }
          }

          setLoading(false);
          isInitialized.current = true;
          return;
        }

        // Skip regular initialization if already done and we have a user
        if (isInitialized.current && currentUserId.current) {
          return;
        }

        // Check if this is an admin impersonation request
        const urlParams = new URLSearchParams(window.location.search);
        const isAdminImpersonate = urlParams.get('admin_impersonate') === 'true';

        // If this is an admin impersonation request, sign out first
        if (isAdminImpersonate) {
          console.log('🔐 Admin impersonation detected - signing out current session...');
          await supabase.auth.signOut();
          // Remove the admin_impersonate param to avoid infinite logout loop
          urlParams.delete('admin_impersonate');
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', newUrl);
          setLoading(false);
          return;
        }

        // Fallback: Handle non-whitelisted agency domain that redirected here to start OAuth
        const startAgencyOAuth = urlParams.get('start_agency_oauth') === 'true';
        const agencyOriginParam = urlParams.get('agency_origin');

        if (startAgencyOAuth && agencyOriginParam) {
          console.log('🏢 Agency login OAuth fallback request:', { agencyOrigin: agencyOriginParam });

          // Validate agency_origin — prevent open-redirect token hijacking
          let agencyHostname: string;
          try {
            const parsed = new URL(agencyOriginParam);
            if (parsed.protocol !== 'https:') throw new Error('Only https origins are allowed');
            agencyHostname = parsed.hostname;
          } catch (err) {
            console.error('🚫 Invalid agency_origin:', agencyOriginParam, err);
            setLoading(false);
            isInitialized.current = true;
            return;
          }

          const { data: knownAgency, error: agencyLookupError } = await supabase
            .from('agencies')
            .select('id')
            .eq('custom_domain', agencyHostname)
            .maybeSingle();

          if (agencyLookupError || !knownAgency) {
            console.error('🚫 Unrecognised agency_origin:', agencyOriginParam);
            setLoading(false);
            isInitialized.current = true;
            return;
          }

          sessionStorage.setItem('oauth_agency_origin', agencyOriginParam);
          window.history.replaceState({}, '', '/fly-app');

          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: `${window.location.origin}/fly-app`,
              queryParams: { access_type: 'offline', prompt: 'select_account' },
              scopes: 'email profile https://www.googleapis.com/auth/business.manage',
            }
          });

          if (error) {
            console.error('Error starting agency OAuth fallback:', error);
            sessionStorage.removeItem('oauth_agency_origin');
            toast({
              title: "Authentication Error",
              description: "Failed to start sign-in. Please try again.",
              variant: "destructive"
            });
          }
          return;
        }

        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
          // If we have OAuth tokens in the URL but session fetch failed,
          // the tokens might be invalid or expired
          if (hasOAuthTokens) {
            toast({
              title: "Authentication Error",
              description: "Failed to complete sign-in. Please try again.",
              variant: "destructive"
            });
          }
        } else if (session?.user) {
          // Check for pending agency onboarding token BEFORE auth check
          const pendingOnboardingTokenFB = sessionStorage.getItem('pending_agency_onboarding_token');
          if (pendingOnboardingTokenFB) {
            console.log('🔗 Pending agency onboarding token found (fallback), redirecting');
            sessionStorage.removeItem('pending_agency_onboarding_token');
            setUser(session.user);
            currentUserId.current = session.user.id;
            setSentryUser({ id: session.user.id, email: session.user.email });
            window.location.href = buildNavigationPath(`/agency-onboarding?token=${pendingOnboardingTokenFB}`);
            return;
          }

          // Check agency authorization before allowing access
          const agencyAuth = await checkAgencyAuthorization(session.user.id);
          if (!agencyAuth.authorized) {
            console.log('❌ User not authorized for agency domain, signing out...');
            // Preserve agency onboarding token before sign-out
            const urlParams3 = new URLSearchParams(window.location.search);
            const onboardingToken3 = urlParams3.get('token');
            if (window.location.pathname.includes('agency-onboarding') && onboardingToken3) {
              sessionStorage.setItem('pending_agency_onboarding_token', onboardingToken3);
            }
            await supabase.auth.signOut();
            toast({
              title: "Access Denied",
              description: agencyAuth.reason || "You don't have access to this portal.",
              variant: "destructive"
            });
            // Don't set user - leave as null
          } else {
            setUser(session.user);
            currentUserId.current = session.user.id;
            setSentryUser({ id: session.user.id, email: session.user.email });

            // If user exists, ensure their profile is created (non-blocking)
            ensureUserProfile(session.user).catch(err =>
              console.error('Profile setup error (non-critical):', err)
            );

            // Check for pending agency onboarding token (from Access Denied → Try Again flow)
            const pendingOnboardingTokenFallback = sessionStorage.getItem('pending_agency_onboarding_token');
            if (pendingOnboardingTokenFallback) {
              sessionStorage.removeItem('pending_agency_onboarding_token');
              console.log('🔗 Redirecting to agency onboarding with preserved token (fallback path)');
              window.location.href = buildNavigationPath(`/agency-onboarding?token=${pendingOnboardingTokenFallback}`);
              return;
            }

            // Pricing-intent consumer — belt-and-braces call so that a
            // user landing on /fly-app with a cached session still gets
            // routed through the funnel bootstrap even if
            // onAuthStateChange never fires a fresh SIGNED_IN event
            // (which happens when the incoming OAuth session has the
            // same user.id as the localStorage cache). Safe to call
            // from here because consumePricingIntent is a one-shot
            // read+delete — the sister call in onAuthStateChange
            // becomes a no-op if we win the race.
            if (await handlePostOAuthPricingIntent()) {
              return;
            }
          }
        } else {
          setUser(null);
          currentUserId.current = undefined;
        }
      } catch (error) {
        console.error('Session initialization error:', error);
      } finally {
        setLoading(false);
        isInitialized.current = true;
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.email);

        try {
          const previousUserId = currentUserId.current;
          const newUserId = session?.user?.id;

          // Pricing-intent consumer MUST run for EVERY auth event with a
          // session, not just SIGNED_IN. Real-world flow that broke:
          //   1. User with a cached localStorage session hits /fly-app after
          //      OAuth. Supabase restores the session from cache and fires
          //      INITIAL_SESSION (not SIGNED_IN) since the user hasn't
          //      changed.
          //   2. The SIGNED_IN branch was skipped, the intent stayed in
          //      sessionStorage, and LocalSEOApp mounted on the wrong route
          //      pinned to "Setting up your workspace…".
          // Widening to any event lets INITIAL_SESSION + SIGNED_IN + TOKEN_
          // REFRESHED all pick the intent up. consumePricingIntent is a
          // one-shot read+delete, so at most one of these actually processes
          // the intent per lifecycle. Skipping SIGNED_OUT explicitly since
          // that path shouldn't fire the bootstrap.
          if (event !== 'SIGNED_OUT' && session?.user) {
            if (await handlePostOAuthPricingIntent()) {
              // Bootstrap is navigating away; skip the rest of the handler.
              return;
            }
          }

          // IMPORTANT: Only update user state if the user ID actually changed
          // This prevents unnecessary re-renders and cascading state updates
          if (previousUserId !== newUserId || event === 'SIGNED_OUT') {
            // Only run these actions when user actually changes
            if (event === 'SIGNED_IN' && session?.user) {
              // Check for pending agency onboarding token BEFORE auth check
              const pendingOnboardingTokenAuth = sessionStorage.getItem('pending_agency_onboarding_token');
              if (pendingOnboardingTokenAuth) {
                console.log('🔗 Pending agency onboarding token found (auth change), redirecting');
                sessionStorage.removeItem('pending_agency_onboarding_token');
                setUser(session.user);
                currentUserId.current = newUserId;
                setSentryUser({ id: session.user.id, email: session.user.email });
                window.location.href = buildNavigationPath(`/agency-onboarding?token=${pendingOnboardingTokenAuth}`);
                return;
              }

              // Check agency authorization BEFORE setting user state
              const agencyAuth = await checkAgencyAuthorization(session.user.id);
              if (!agencyAuth.authorized) {
                console.log('❌ User not authorized for agency domain in auth change, signing out...');
                // Preserve agency onboarding token before sign-out
                const urlParams4 = new URLSearchParams(window.location.search);
                const onboardingToken4 = urlParams4.get('token');
                if (window.location.pathname.includes('agency-onboarding') && onboardingToken4) {
                  sessionStorage.setItem('pending_agency_onboarding_token', onboardingToken4);
                }
                await supabase.auth.signOut();
                setUser(null);
                currentUserId.current = undefined;
                toast({
                  title: "Access Denied",
                  description: agencyAuth.reason || "You don't have access to this portal.",
                  variant: "destructive"
                });
                return;
              }

              // User is authorized, set the user state
              setUser(session.user);
              currentUserId.current = newUserId;
              setSentryUser({ id: session.user.id, email: session.user.email });

              // Fire `signup_completed` once if this SIGNED_IN event is the
              // user's very first session (created within last 30s). The
              // tracking is async + non-blocking: it spawns a detached
              // IIFE so a slow agency_name lookup can NEVER stall the
              // SIGNED_IN handler. Previous version awaited the agency
              // query inline — for brand-new ProductHunt users the
              // query could hang during the post-OAuth token-swap
              // window (supabase-js queues queries while the new
              // session settles), which kept setLoading(false) in the
              // finally from ever running and pinned the page on the
              // "Loading…" spinner.
              try {
                const createdAt = session.user.created_at
                  ? new Date(session.user.created_at).getTime()
                  : 0;
                const ageMs = Date.now() - createdAt;
                const isBrandNew = createdAt > 0 && ageMs >= 0 && ageMs < 30_000;
                const signedInUserId = session.user.id;
                const signedInEmail = session.user.email ?? null;
                const signedInCreatedAt = session.user.created_at ?? null;
                const sourceFromUrl =
                  typeof window !== 'undefined'
                    ? new URLSearchParams(window.location.search).get('source')
                    : null;
                // A private page (/agent/<page>/<token>, /app/l/<location>) is
                // stored as its template, never with the token: lib/privatePaths.ts.
                const pageSlug =
                  typeof window !== 'undefined' ? maskPrivatePath(window.location.pathname) : null;
                // Detached: no await on this IIFE so the SIGNED_IN
                // handler reaches its `finally { setLoading(false) }`
                // immediately. The agency_name lookup also gets a 3s
                // race-timeout so the tracking call never hangs forever.
                void (async () => {
                  let agencyName: string | null = null;
                  try {
                    const lookup = supabase
                      .from('agencies')
                      .select('name')
                      .eq('owner_id', signedInUserId)
                      .maybeSingle();
                    const timeout = new Promise<{ data: null }>((resolve) =>
                      setTimeout(() => resolve({ data: null }), 3000),
                    );
                    const result = await Promise.race([lookup, timeout]);
                    const row = (result as { data?: { name?: string | null } | null }).data;
                    if (row) agencyName = row.name ?? null;
                  } catch {
                    // Best-effort, ignore.
                  }
                  try {
                    // Brand-new account in last 30s → signup_completed.
                    // Returning OAuth sign-in → signin_completed. Both
                    // land in the activity feed so ops sees the full
                    // signin/signup funnel, not just first-time signups.
                    void trackEvent(
                      isBrandNew ? 'signup_completed' : 'signin_completed',
                      {
                        page_slug: pageSlug,
                        email: signedInEmail,
                        agency_name: agencyName,
                        source: sourceFromUrl,
                        created_at_iso: signedInCreatedAt,
                      },
                    );
                  } catch {
                    // Tracking is best-effort.
                  }
                })();
              } catch {
                // Tracking is best-effort, never block sign-in on a bad clock.
              }

              // Run profile setup in background - don't block the UI
              ensureUserProfile(session.user).catch(err =>
                console.error('Profile setup error (non-blocking):', err)
              );

              // Only show toast once per session to avoid repeated notifications
              if (!signInToastShown.current) {
                signInToastShown.current = true;
                toast({
                  title: "Welcome!",
                  description: `Signed in as ${session.user.email}`
                });
              }

              // Check for pending agency onboarding token (from Access Denied flow)
              const pendingOnboardingToken = sessionStorage.getItem('pending_agency_onboarding_token');
              if (pendingOnboardingToken) {
                sessionStorage.removeItem('pending_agency_onboarding_token');
                window.location.href = buildNavigationPath(`/agency-onboarding?token=${pendingOnboardingToken}`);
                return;
              }

              // Check for pending team invitation token (workspace or agency)
              const pendingInviteToken = sessionStorage.getItem('pending_invite_token');
              const pendingAgencyInviteToken = sessionStorage.getItem('pending_agency_invite_token');
              const pendingOrigin = sessionStorage.getItem('pending_invite_origin');

              if (pendingAgencyInviteToken) {
                // Agency team invite - redirect with agency-invite param
                sessionStorage.removeItem('pending_agency_invite_token');
                sessionStorage.removeItem('invite_email');
                sessionStorage.removeItem('pending_invite_origin');
                // Redirect back to the agency domain if the invite originated there
                const inviteBaseUrl = pendingOrigin || buildAppPath('/');
                window.location.href = `${inviteBaseUrl}/?agency-invite=${pendingAgencyInviteToken}`;
              } else if (pendingInviteToken) {
                // Workspace team invite - redirect with invite-link param
                sessionStorage.removeItem('pending_invite_token');
                sessionStorage.removeItem('invite_email');
                sessionStorage.removeItem('pending_invite_origin');
                // Redirect back to the agency domain if the invite originated there
                const inviteBaseUrl = pendingOrigin || buildAppPath('/');
                window.location.href = `${inviteBaseUrl}/?invite-link=${pendingInviteToken}`;
              } else if (await handlePostOAuthPricingIntent()) {
                return;
              }
            } else {
              // Not a SIGNED_IN event with user, just update state
              setUser(session?.user ?? null);
              currentUserId.current = newUserId;
            }
          }

          if (event === 'SIGNED_OUT') {
            // Reset the sign-in toast flag
            signInToastShown.current = false;
            // Reset initialization state
            isInitialized.current = false;
            currentUserId.current = undefined;
            clearSentryUser();

            // Clear all cached data
            localStorage.removeItem('gmb_imported_locations');
            localStorage.removeItem('gmb_has_locations');
            localStorage.removeItem('supabase.auth.token');
            // Clear Supabase auth tokens (dynamic - works across all environments)
            try {
              Object.keys(localStorage)
                .filter(key => key.startsWith('sb-') && key.endsWith('-auth-token'))
                .forEach(key => localStorage.removeItem(key));
            } catch { /* ignore */ }
            // Note: Do NOT clear admin_impersonation here - it's needed during
            // the impersonation flow when switching between users.
            // It will be cleared explicitly when the admin ends impersonation
            // or signs out via the AdminUserSwitcher.

            // Skip toast if this SIGNED_OUT was emitted by setSession() during
            // an OAuth callback — it fires SIGNED_OUT before SIGNED_IN and is
            // not a real sign-out.
            if (!processingOAuthCallback.current) {
              toast({
                title: "Signed out",
                description: "You have been successfully signed out"
              });
            }
          }
        } catch (error) {
          console.error('Auth state change error:', error);
        } finally {
          // Always clear loading state
          setLoading(false);
          console.log('Auth loading state cleared');
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Once per user per page load: getSession and the auth events each start it,
  // and every start did the same reads and RPCs (walk 05 Oct 26, item 9).
  // A run with a failed step resolves false, so the next event runs it again.
  const ensureUserProfile = (user: User) => onceForUser(user.id, () => runUserProfileSetup(user));

  const signInWithGoogle = async () => {
    try {
      setLoading(true);

      // Store the current URL to redirect back after OAuth
      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem('oauth_redirect_url', currentPath);

      // Determine the correct redirect URL based on environment
      const isLocalhost = window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';

      // Check if we're on a white-label agency domain
      const hostname = window.location.hostname;
      const isMainDomain = hostname.includes('fly-social.com') ||
                           hostname.includes('fly-local-boost') ||
                           hostname.includes('vercel.app');
      const isAgencyDomain = !isLocalhost && !isMainDomain;

      let redirectUrl: string;

      if (isAgencyDomain) {
        // Check if this agency domain is whitelisted for direct OAuth
        const { isAgencyOAuthWhitelisted } = await import('@/utils/gmbOAuthUtils');
        if (isAgencyOAuthWhitelisted()) {
          // Whitelisted — use current origin with agency-aware path (no /fly-app prefix)
          redirectUrl = `${window.location.origin}${buildAppPath('/')}`;
        } else {
          // Not whitelisted — fallback to fly-social.com proxy
          const agencyOrigin = encodeURIComponent(window.location.origin);
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
          const isStaging = supabaseUrl.includes('kniikxfczzcvdyaphomu') ||
                            supabaseUrl.includes('tfydzhjtnqvgusqdchwg');
          const mainAppDomain = isStaging ? 'https://staging.fly-social.com' : 'https://www.fly-social.com';
          const agencyOAuthUrl = `${mainAppDomain}/fly-app?start_agency_oauth=true&agency_origin=${agencyOrigin}`;
          console.log('🏢 Agency login OAuth (fallback): redirecting to main domain', { agencyOAuthUrl });
          // Paint the agency-branded overlay before the cross-origin nav so
          // the URL-bar swap to fly-social.com happens behind the agency
          // logo + spinner instead of in front of the dashboard UI.
          const { showAgencyOAuthOverlay } = await import('@/utils/gmbOAuthUtils');
          await showAgencyOAuthOverlay();
          window.location.href = agencyOAuthUrl;
          return;
        }
      } else {
        redirectUrl = `${window.location.origin}/fly-app`;
      }

      console.group('🔍 OAuth Config');
      console.log('Environment:', isLocalhost ? 'localhost' : 'deployed');
      console.log('Redirect URL:', redirectUrl);
      console.log('Current origin:', window.location.origin);
      console.groupEnd();

      // Fire signup_started BEFORE the redirect leaves the page so the
      // intent is captured even if the visitor bounces from Google's
      // consent screen. UTM auto-attaches via trackEvent so the activity
      // feed row carries attribution. Detached: don't await, the OAuth
      // redirect is what matters.
      try {
        void trackEvent('signup_started', {
          provider: 'google',
          page_slug: typeof window !== 'undefined' ? maskPrivatePath(window.location.pathname) : null,
          source: typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('source')
            : null,
        });
      } catch {
        // Tracking is best-effort.
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
          scopes: 'email profile https://www.googleapis.com/auth/business.manage',
          // Skip nonce validation for localhost to avoid site URL mismatch issues
          skipBrowserRedirect: false,
        }
      });

      if (error) {
        throw error;
      }
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      toast({
        title: "Sign-in failed",
        description: error.message || "Failed to sign in with Google",
        variant: "destructive"
      });
      setLoading(false);
    }
    // Don't set loading to false here - let the auth state change handle it
  };

  const signOut = async () => {
    try {
      // Clear admin impersonation data before signing out
      localStorage.removeItem('admin_impersonation');

      // Clear any per-user role caches the app maintains in localStorage
      // so the next user signing in on the same browser doesn't inherit
      // a stale role state. `flyLeadGenOnly:<uid>` is keyed by user id;
      // we wipe every key with that prefix.
      try {
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith('flyLeadGenOnly:')) keys.push(k);
        }
        keys.forEach((k) => localStorage.removeItem(k));
      } catch {
        // Storage access denied — non-fatal.
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
    } catch (error: any) {
      console.error('Sign-out error:', error);
      toast({
        title: "Sign-out failed",
        description: error.message || "Failed to sign out"
      });
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signInWithGoogle,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {shouldSplashForAppSumo || shouldSplashForAppsumoLicense ? <AppSumoRedirectSplash /> : children}
    </AuthContext.Provider>
  );
};

/** Full-page splash rendered by AuthProvider whenever a pending
 *  AppSumo code is detected on a non-redeem route. Suppresses the
 *  "flash of dashboard" that used to appear between OAuth return and
 *  the compulsory-flow redirect. Kept intentionally simple — this
 *  screen is on-screen for <200ms in the happy path. */
function AppSumoRedirectSplash() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <img
          src="/lovable-uploads/2e7bf5aa-9c7b-41ea-b64a-48b87f3dc01d.png"
          alt="Fly"
          className="h-12 w-12 md:h-14 md:w-14 drop-shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
        />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
          <span>Setting up your redemption…</span>
        </div>
      </div>
    </div>
  );
}
